/**
 * Upload service for handling file uploads
 * This service prepares images for upload to the backend Firebase Storage
 */
const uploadService = {
  /**
   * Prepare a menu item image for upload
   * @param {File|string} file - Image as File object or base64 string
   * @returns {Promise<string>} - Base64 image data (ready for backend processing)
   */
  uploadMenuImage: async (file) => {
    try {
      // If it's a File object, convert to base64
      if (file instanceof File) {
        // Validate file first
        const validation = uploadService.validateImage(file);
        if (!validation.isValid) {
          throw new Error(validation.message);
        }
        
        return await convertFileToBase64(file);
      }
      
      // If it's already a base64 string, just return it
      if (typeof file === 'string' && file.startsWith('data:image')) {
        return file;
      }
      
      throw new Error('Invalid file format');
    } catch (error) {
      console.error('Error preparing menu image:', error);
      throw new Error(`Failed to process image: ${error.message}`);
    }
  },

  /**
   * Validate image size and type (Browser-compatible version)
   * @param {File|string} file - Image to validate
   * @returns {Object} Validation result
   */
  validateImage: (file) => {
    // File object validation
    if (file instanceof File) {
      // Size check (5MB)
      if (file.size > 5 * 1024 * 1024) {
        return { 
          isValid: false, 
          message: 'Image must be less than 5MB' 
        };
      }
      
      // Type check
      if (!file.type.startsWith('image/')) {
        return { 
          isValid: false, 
          message: 'Only image files are allowed' 
        };
      }
      
      return { isValid: true };
    }
    
    // Base64 string validation (Browser-compatible - no Buffer)
    if (typeof file === 'string' && file.startsWith('data:image')) {
      try {
        // Calculate approximate size without Buffer (browser-safe method)
        const base64Data = file.replace(/^data:image\/\w+;base64,/, '');
        const padding = (base64Data.match(/=/g) || []).length;
        const imageSizeInBytes = (base64Data.length * 3) / 4 - padding;
        
        if (imageSizeInBytes > 5 * 1024 * 1024) {
          return { 
            isValid: false, 
            message: 'Image must be less than 5MB' 
          };
        }
        
        return { isValid: true };
      } catch  { // Fixed: renamed 'error' to 'validationError'
        return { 
          isValid: false, 
          message: 'Invalid base64 image data' 
        };
      }
    }
    
    return { 
      isValid: false, 
      message: 'Invalid image format' 
    };
  },

  /**
   * Prepare a profile image for upload
   * @param {File|string} file - Image as File object or base64 string
   * @returns {Promise<string>} - Base64 image data
   */
  uploadProfileImage: async (file) => {
    try {
      // Validate the file first
      const validation = uploadService.validateImage(file);
      if (!validation.isValid) {
        throw new Error(validation.message);
      }
      
      // If it's a File object, convert to base64
      if (file instanceof File) {
        return await convertFileToBase64(file);
      }
      
      // If it's already a base64 string, just return it
      if (typeof file === 'string' && file.startsWith('data:')) {
        return file;
      }
      
      throw new Error('Invalid file format');
    } catch (error) {
      console.error('Error preparing profile image:', error);
      throw new Error(`Failed to process image: ${error.message}`);
    }
  },

  /**
   * Upload vendor logo with optimization (same pattern as menu images)
   * @param {File} file - Image file to upload
   * @returns {Promise<string>} Optimized base64 image data
   */
  uploadVendorLogo: async (file) => {
    try {
      // Validate image first
      const validation = uploadService.validateImage(file);
      if (!validation.isValid) {
        throw new Error(validation.message);
      }

      // Convert to optimized base64 (same pattern as menu images)
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const img = new Image();
          img.onload = () => {
            // Create canvas to resize/optimize image
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            // Set maximum dimensions for vendor logos (smaller than menu images)
            const maxWidth = 500;
            const maxHeight = 500;
            
            let { width, height } = img;
            
            // Calculate new dimensions (maintain aspect ratio)
            if (width > height) {
              if (width > maxWidth) {
                height = (height * maxWidth) / width;
                width = maxWidth;
              }
            } else {
              if (height > maxHeight) {
                width = (width * maxHeight) / height;
                height = maxHeight;
              }
            }
            
            canvas.width = width;
            canvas.height = height;
            
            // Draw and compress image
            ctx.drawImage(img, 0, 0, width, height);
            
            // Convert to base64 with compression (same as menu images)
            const compressedBase64 = canvas.toDataURL('image/jpeg', 0.7);
            resolve(compressedBase64);
          };
          img.onerror = () => reject(new Error('Failed to load image'));
          img.src = reader.result;
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      });

    } catch (error) {
      console.error('Error uploading vendor logo:', error);
      throw error;
    }
  },

  /**
   * Prepare a category image for upload
   * @param {File|string} file - Image as File object or base64 string
   * @returns {Promise<string>} - Base64 image data
   */
  uploadCategoryImage: async (file) => {
    try {
      // Same implementation as uploadMenuImage
      if (file instanceof File) {
        const validation = uploadService.validateImage(file);
        if (!validation.isValid) {
          throw new Error(validation.message);
        }
        return await convertFileToBase64(file);
      }
      
      if (typeof file === 'string' && file.startsWith('data:')) {
        return file;
      }
      
      throw new Error('Invalid file format');
    } catch (error) {
      console.error('Error preparing category image:', error);
      throw new Error(`Failed to process image: ${error.message}`);
    }
  },

  /**
   * Convert a data URL to a File object
   * @param {string} dataUrl - Base64 data URL
   * @param {string} filename - Desired filename
   * @returns {File} - File object
   */
  dataURLtoFile: (dataUrl, filename) => {
    const arr = dataUrl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    
    return new File([u8arr], filename, { type: mime });
  }
};

/**
 * Convert a File object to base64 string
 * @param {File} file - File object
 * @returns {Promise<string>} - Base64 string
 */
const convertFileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

export default uploadService;